import { useEffect, useRef, useId, useImperativeHandle, forwardRef, type ReactNode, type KeyboardEvent } from 'react';
import { MenuV2 } from '@figma/fpl-components';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { styled } from '@/UI/theme.exports';
import { AppMenuItem } from '@/UI/AppContextMenu/AppMenu.styles';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { MenuV2Items } from '@/UI/AppContextMenu/MenuV2Items';
import { CaretIcon } from '@/UI/Icons/CaretIcon';
import { color } from '@/colors';
import { EllipsisText } from '@/UI/EllipsisText/EllipsisText';
import { FlexCenVer } from '@/UI/styles';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { PARENT_MENU_ITEM_DATA_ATTRIBUTE, useParentMenuHelper } from './hooks/useParentMenuHelper';
import type { MenuAction } from '@/components/Menu/Actions';
import type { SxProps, Theme } from '@/UI/theme.exports';

export interface ParentMenuItemHandle {
  /** Imperatively collapse this submenu — e.g. after a child item commits an action. */
  close: () => void;
}

interface ParentMenuItemProps {
  label: string;
  /** Optional leading icon rendered before the submenu label. */
  lead?: ReactNode;
  /** Optional trailing element rendered between the label and the caret (e.g. a "New" tag). */
  badge?: ReactNode;
  children?: ReactNode;
  /**
   * Submenu contents as data. Preferred over `children`: flag-off renders these via `Actions`
   * (MUI), flag-on renders them as `MenuV2` items inside a `MenuV2.SubMenu`. When omitted, the
   * pre-rendered `children` are used instead.
   */
  items?: MenuAction[];
  id?: string;
  onClose?: () => void;
  contextMenuWidth?: string;
  offset?: { top?: string; left?: string };
  sticky?: boolean;
  disabled?: boolean;
  onOpen?: () => void;
  sx?: SxProps<Theme>;
  autoFocus?: boolean;
  onKeyDown?: (e: KeyboardEvent) => void;
}

const LegacyParentMenuItem = forwardRef<ParentMenuItemHandle, ParentMenuItemProps>(function LegacyParentMenuItem(
  {
    label,
    lead,
    badge,
    children,
    items,
    id,
    onClose,
    contextMenuWidth,
    offset,
    sticky,
    disabled = false,
    onOpen,
    sx,
    autoFocus = true,
    onKeyDown,
  },
  ref,
) {
  const menuItemRef = useRef<HTMLLIElement>(null);
  const menuItemId = useId();
  const normMenuItemId = id ?? menuItemId;

  const { close, isOpen, shouldFocusOnExit, handleMouseEnter, handleMouseLeave, handleItemKeyDown, handleMenuKeyDown } =
    useParentMenuHelper({
      menuItemRef,
      sticky,
      onKeyDown,
    });

  useImperativeHandle(ref, () => ({ close }), [close]);

  const onMenuClose = () => {
    close();
    onClose?.();
  };

  useEffect(() => {
    if (isOpen) {
      onOpen?.();
    }
  }, [isOpen, onOpen]);

  return (
    <AppMenuItem
      onKeyDown={handleItemKeyDown}
      ref={menuItemRef}
      sx={{
        ...(isOpen && {
          backgroundColor: `${color.Black84} !important`,
        }),
        ...sx,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      id={normMenuItemId}
      disabled={disabled}
      {...{ [PARENT_MENU_ITEM_DATA_ATTRIBUTE]: 'true' }}
    >
      <FlexCenVer sx={{ flex: 1, minWidth: 0, gap: 0.75 }}>
        {lead}
        <EllipsisText sx={{ minWidth: 0 }} variant="body-sm-rg" placement="bottom">
          {label}
        </EllipsisText>
        {badge}
      </FlexCenVer>
      <CaretIcon width={12} height={12} style={{ transform: 'rotate(-90deg)' }} />
      <AppContextMenu
        slotProps={{
          transition: {
            onEntered: (node) => {
              if (node && autoFocus) {
                const firstMenuItem = node.querySelector('li');
                firstMenuItem?.focus();
              }
            },
            onExited: () => {
              if (shouldFocusOnExit) {
                menuItemRef.current?.focus();
              }
            },
          },
        }}
        disableRestoreFocus
        onKeyDown={handleMenuKeyDown}
        sx={{
          pointerEvents: 'none',
          '& .MuiList-root': {
            pointerEvents: 'auto',
          },
        }}
        MenuListProps={{ 'aria-labelledby': normMenuItemId }}
        anchorEl={menuItemRef.current}
        open={isOpen}
        onClose={onMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        width={contextMenuWidth}
        offset={offset}
        items={items}
      >
        {items ? null : children}
      </AppContextMenu>
    </AppMenuItem>
  );
});

/** Retain Weave submenu highlights while FPL's safe triangle keeps the trigger active. */
const SubmenuTitle = styled('span')({
  '[role="menuitem"][data-fpl-triangle-active]:not([data-fpl-surface-trigger]):not([aria-disabled="true"]):has(&) > span':
    {
      backgroundColor: token.bgBrand,
      color: token.textOnbrand,
      '--fpl-icon-color': token.iconOnbrand,
    },
});

/**
 * Flag-on submenu: `MenuV2.SubMenu` owns hover/keyboard/positioning/safe-triangle, so the elaborate
 * `useParentMenuHelper` orchestration is unnecessary. Prefer `items` (rendered as MenuV2 items);
 * `children` are rendered verbatim as a fallback for not-yet-migrated composite submenus.
 *
 * `MenuV2.SubMenu`'s `disabled` still opens the flyout (children dimmed). Product wants a dead row
 * when the parent is disabled (e.g. Download/Clear with nothing to act on), so render a plain
 * disabled item instead — same as MUI's disabled `AppMenuItem`.
 */
function FplParentMenuItem({ label, lead, badge, children, items, disabled = false, onClose }: ParentMenuItemProps) {
  if (disabled) {
    return (
      <MenuV2.Item disabled lead={lead} trail={badge} onClick={() => {}}>
        {label}
      </MenuV2.Item>
    );
  }

  return (
    <MenuV2.SubMenu title={<SubmenuTitle>{label}</SubmenuTitle>} titleLead={lead} titleTrail={badge}>
      {items ? <MenuV2Items items={items} requestClose={() => onClose?.()} /> : children}
    </MenuV2.SubMenu>
  );
}

const ParentMenuItem = forwardRef<ParentMenuItemHandle, ParentMenuItemProps>(function ParentMenuItem(props, ref) {
  const isFplMigrationEnabled = useFplMigrationFlag();
  return isFplMigrationEnabled ? <FplParentMenuItem {...props} /> : <LegacyParentMenuItem {...props} ref={ref} />;
});

export default ParentMenuItem;
